import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Cloud, Mail, Lock, AlertCircle, Globe2, Eye, EyeOff, Moon, Sun, CheckCircle2, MapPin, Globe, X, AlertTriangle, ChevronDown, MailCheck, ArrowRight, Loader2 } from 'lucide-react';
import { useAuthStore } from '../stores/auth-store';
import { useI18n } from '../i18n';
import Captcha, { CaptchaHandle } from '../components/auth/Captcha';
import { useTenantOptional } from '../contexts/TenantContext';
import { TENANT_CONFIG, TenantId } from '../config/tenant';
import { supabase } from '../config/supabase';
import '../styles/auth.css';

const RegisterPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showTenantModal, setShowTenantModal] = useState(false);
  const { signUp } = useAuthStore();
  const navigate = useNavigate();
  const { tenant: tenantParam } = useParams<{ tenant: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tenantContext = useTenantOptional();
  const { t, lang, switchTo } = useI18n();
  const [captchaValue, setCaptchaValue] = useState('');
  const [captchaError, setCaptchaError] = useState('');
  const [isEmailConfirmationPending, setIsEmailConfirmationPending] = useState(searchParams.get('status') === 'check-email');
  const [confirmationEmail, setConfirmationEmail] = useState(searchParams.get('email') || '');
  const [isCheckingConfirmation, setIsCheckingConfirmation] = useState(false);
  const [confirmationNotice, setConfirmationNotice] = useState('');
  const captchaRef = useRef<CaptchaHandle | null>(null);

  // 获取当前租户
  const currentTenant: TenantId = (tenantParam === 'cn' || tenantParam === 'intl') 
    ? tenantParam 
    : (tenantContext?.tenant || 'cn');
  const portalPath = `/${currentTenant}/portal`;

  useEffect(() => {
    if (searchParams.get('status') !== 'check-email') return;
    setIsEmailConfirmationPending(true);
    const queryEmail = searchParams.get('email');
    if (queryEmail) {
      setConfirmationEmail(queryEmail);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!isEmailConfirmationPending) return;

    let cancelled = false;

    const redirectIfConfirmed = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!cancelled && session?.user) {
          navigate(portalPath, { replace: true });
        }
      } catch (error) {
        console.warn('检查邮箱确认状态失败:', error);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') && session?.user) {
        navigate(portalPath, { replace: true });
      }
    });

    void redirectIfConfirmed();
    const intervalId = window.setInterval(redirectIfConfirmed, 3000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      subscription.unsubscribe();
    };
  }, [isEmailConfirmationPending, navigate, portalPath]);

  // Password strength check
  const getPasswordStrength = (pwd: string): { level: number; label: string; color: string } => {
    if (!pwd) return { level: 0, label: '', color: '' };
    let score = 0;
    if (pwd.length >= 6) score++;
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    
    if (score <= 2) return { level: 1, label: t('passwordWeak'), color: '#ff6b6b' };
    if (score <= 3) return { level: 2, label: t('passwordMedium'), color: '#ffa726' };
    return { level: 3, label: t('passwordStrong'), color: '#66bb6a' };
  };

  const passwordStrength = getPasswordStrength(password);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Basic validation
    if (!email.trim()) {
      setError(t('emailRequired'));
      return;
    }
    
    if (!password.trim()) {
      setError(t('passwordRequired'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('passwordsDoNotMatch'));
      return;
    }
    
    if (password.length < 6) {
      setError(t('passwordMinLength'));
      return;
    }

    try {
      setIsLoading(true);
      if (!captchaRef.current?.validate(captchaValue)) {
        setCaptchaError(t('captchaInvalid'));
        setIsLoading(false);
        return;
      }
      setCaptchaError('');
      const normalizedEmail = email.trim().toLowerCase();
      const result = await signUp(normalizedEmail, password, currentTenant);

      if (result.needsEmailConfirmation) {
        setEmail(normalizedEmail);
        setConfirmationEmail(result.email);
        setIsEmailConfirmationPending(true);
        setConfirmationNotice('');
        setPassword('');
        setConfirmPassword('');
        setCaptchaValue('');
        captchaRef.current?.regenerate();
        setSearchParams({ status: 'check-email', email: result.email }, { replace: true });
        return;
      }

      navigate(portalPath, { replace: true });
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error instanceof Error 
        ? error.message.includes('Database error')
          ? t('unableToCreateAccount')
          : error.message
        : t('failedToCreateAccount');
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // 切换到另一个区域
  const handleSwitchTenant = (targetTenant: TenantId) => {
    setShowTenantModal(false);
    if (targetTenant !== currentTenant) {
      navigate(`/${targetTenant}/register`);
    }
  };

  const handleCheckConfirmation = async () => {
    setIsCheckingConfirmation(true);
    setConfirmationNotice('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        navigate(portalPath, { replace: true });
        return;
      }
      setConfirmationNotice(t('confirmationPendingNotice'));
    } catch (error) {
      console.error('邮箱确认状态检查失败:', error);
      setConfirmationNotice(t('confirmationCheckFailed'));
    } finally {
      setIsCheckingConfirmation(false);
    }
  };

  const handleEditEmail = () => {
    setIsEmailConfirmationPending(false);
    setConfirmationNotice('');
    setSearchParams({}, { replace: true });
    captchaRef.current?.regenerate();
  };

  const isFormValid = email.trim() && 
    password.trim() && 
    confirmPassword.trim() && 
    password === confirmPassword && 
    captchaRef.current?.validate(captchaValue);

  if (isEmailConfirmationPending) {
    return (
      <div className={`auth-background ${!isDarkMode ? 'light-mode' : ''}`}>
        <div className="auth-orb auth-orb-1" />
        <div className="auth-orb auth-orb-2" />
        <div className="auth-orb auth-orb-3" />

        <div className="min-h-screen flex items-center justify-center p-4 relative z-10">
          <div className="auth-card auth-card-enter auth-confirmation-card w-full max-w-[440px] p-10">
            <div className="text-center mb-7">
              <div className="flex justify-center mb-5">
                <div className="auth-confirmation-icon">
                  <MailCheck strokeWidth={1.8} />
                </div>
              </div>
              <div className="flex justify-center mb-4">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-gray-100 to-gray-50 rounded-full text-sm font-medium text-gray-700 border border-gray-200">
                  <span className="text-lg">{TENANT_CONFIG[currentTenant].flag}</span>
                  <span>{t(`tenant.${currentTenant}`)}</span>
                </div>
              </div>
              <h1 className="auth-title">{t('checkEmailTitle')}</h1>
              <p className="auth-subtitle">{t('checkEmailSubtitle')}</p>
            </div>

            <div className="auth-email-panel">
              <div className="auth-email-panel-icon">
                <Mail size={20} />
              </div>
              <div className="min-w-0">
                <p className="auth-email-label">{t('confirmationEmailSentTo')}</p>
                <p className="auth-email-address">{confirmationEmail || email}</p>
              </div>
            </div>

            <div className="auth-confirmation-steps">
              <div className="auth-confirmation-step">
                <span>1</span>
                <p>{t('openEmailToConfirm')}</p>
              </div>
              <div className="auth-confirmation-step">
                <span>2</span>
                <p>{t('autoRedirectAfterConfirm')}</p>
              </div>
            </div>

            {confirmationNotice && (
              <div className="auth-info-message">
                <AlertCircle size={16} />
                <span>{confirmationNotice}</span>
              </div>
            )}

            <div className="space-y-3 mt-6">
              <button
                type="button"
                onClick={handleCheckConfirmation}
                disabled={isCheckingConfirmation}
                className="auth-button-primary"
              >
                <span className="flex items-center justify-center gap-2">
                  {isCheckingConfirmation ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <ArrowRight size={18} />
                  )}
                  {isCheckingConfirmation ? t('checkingConfirmation') : t('iveConfirmedContinue')}
                </span>
              </button>
              <button
                type="button"
                onClick={() => navigate(`/${currentTenant}/login`)}
                className="auth-button-secondary"
              >
                {t('backToLogin')}
              </button>
              <button
                type="button"
                onClick={handleEditEmail}
                className="auth-button-ghost"
              >
                {t('editEmail')}
              </button>
            </div>

            <div className="auth-footer mt-8">
              <button
                type="button"
                onClick={() => setIsDarkMode(!isDarkMode)}
                className="auth-footer-button"
              >
                {isDarkMode ? <Sun size={14} /> : <Moon size={14} />}
              </button>
              <button
                type="button"
                onClick={() => switchTo(lang === 'zh' ? 'en' : 'zh')}
                className="auth-footer-button"
              >
                <Globe2 size={14} />
                {lang === 'zh' ? 'English' : '中文'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`auth-background ${!isDarkMode ? 'light-mode' : ''}`}>
      {/* Animated gradient orbs */}
      <div className="auth-orb auth-orb-1" />
      <div className="auth-orb auth-orb-2" />
      <div className="auth-orb auth-orb-3" />
      
      {/* Main content */}
      <div className="min-h-screen flex items-center justify-center p-4 relative z-10">
        <div className="auth-card auth-card-enter w-full max-w-[400px] p-10">
          {/* Logo & Title */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="auth-icon-container">
                <Cloud strokeWidth={1.5} />
              </div>
            </div>
            {/* 租户标识 - 可点击切换 */}
            <div className="flex justify-center mb-4">
              <button
                type="button"
                onClick={() => setShowTenantModal(true)}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-gray-100 to-gray-50 rounded-full text-sm font-medium text-gray-700 border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer"
              >
                <span className="text-lg">{TENANT_CONFIG[currentTenant].flag}</span>
                <span>{t(`tenant.${currentTenant}`)}</span>
                <ChevronDown size={14} className="text-gray-400" />
              </button>
            </div>
            <h1 className="auth-title">{t('createAccount')}</h1>
            <p className="auth-subtitle">
              {t('haveAccount')}{' '}
              <Link to={`/${currentTenant}/login`} className="auth-link">
                {t('signInAction')}
              </Link>
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="auth-error">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          {/* Register Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email Field */}
            <div className="auth-input-group">
              <div className="relative">
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="auth-input"
                  placeholder={t('emailAddress')}
                  autoComplete="email"
                  required
                />
                <Mail size={18} className="auth-input-icon" />
              </div>
            </div>

            {/* Password Field */}
            <div className="auth-input-group">
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="auth-input"
                  placeholder={t('password')}
                  autoComplete="new-password"
                  required
                />
                <Lock size={18} className="auth-input-icon" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="auth-input-action"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {/* Password Strength Indicator */}
              {password && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' }}>
                    <div 
                      className="h-full rounded-full transition-all duration-300"
                      style={{ 
                        width: `${(passwordStrength.level / 3) * 100}%`,
                        backgroundColor: passwordStrength.color
                      }}
                    />
                  </div>
                  <span className="text-xs font-medium" style={{ color: passwordStrength.color }}>
                    {passwordStrength.label}
                  </span>
                </div>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="auth-input-group">
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="auth-input"
                  placeholder={t('confirmPassword')}
                  autoComplete="new-password"
                  required
                />
                <Lock size={18} className="auth-input-icon" />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="auth-input-action"
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {/* Password Match Indicator */}
              {confirmPassword && (
                <div className="mt-2 flex items-center gap-2">
                  {passwordsMatch ? (
                    <>
                      <CheckCircle2 size={14} className="text-green-400" />
                      <span className="text-xs font-medium text-green-400">{t('passwordsMatch')}</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle size={14} className="text-red-400" />
                      <span className="text-xs font-medium text-red-400">{t('passwordsDoNotMatch')}</span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Captcha Field */}
            <div className="auth-input-group">
              <div className="auth-captcha-container">
                <input
                  type="text"
                  value={captchaValue}
                  onChange={(e) => setCaptchaValue(e.target.value)}
                  placeholder={t('captchaPlaceholder')}
                  className="auth-captcha-input"
                />
                <div className="auth-captcha-wrapper">
                  <Captcha 
                    ref={captchaRef} 
                    onChange={() => { 
                      setCaptchaError(''); 
                      setCaptchaValue(''); 
                    }} 
                  />
                </div>
              </div>
              {captchaError && (
                <p className="mt-2 text-sm text-red-400">{captchaError}</p>
              )}
            </div>

            {/* Submit Button */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={isLoading || !isFormValid}
                className="auth-button-primary"
              >
                <span className="flex items-center justify-center">
                  {isLoading && <span className="auth-spinner" />}
                  {isLoading ? t('creatingAccount') : t('createAccountAction')}
                </span>
              </button>
            </div>
          </form>

          {/* Footer Controls */}
          <div className="auth-footer mt-8">
            <button
              type="button"
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="auth-footer-button"
            >
              {isDarkMode ? <Sun size={14} /> : <Moon size={14} />}
            </button>
            <button
              type="button"
              onClick={() => switchTo(lang === 'zh' ? 'en' : 'zh')}
              className="auth-footer-button"
            >
              <Globe2 size={14} />
              {lang === 'zh' ? 'English' : '中文'}
            </button>
          </div>
        </div>
      </div>

      {/* 区域切换模态框 */}
      {showTenantModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-sm mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* 标题 */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800">
                {lang === 'zh' ? '选择服务区域' : 'Select Region'}
              </h3>
              <button 
                onClick={() => setShowTenantModal(false)} 
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            
            {/* 警告提示 */}
            <div className="mx-6 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex gap-2">
                <AlertTriangle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">{lang === 'zh' ? '重要提示' : 'Important Notice'}</p>
                  <p className="mt-1 text-amber-700">
                    {lang === 'zh' 
                      ? '国内与国际账户不互通，请根据您的实际所在区域选择。' 
                      : 'Domestic and international accounts are separate. Please select based on your actual region.'}
                  </p>
                </div>
              </div>
            </div>
            
            {/* 区域选项 */}
            <div className="p-6 space-y-3">
              {/* 国内选项 */}
              <button
                onClick={() => handleSwitchTenant('cn')}
                className={`w-full flex items-center gap-4 p-4 border rounded-xl transition-all ${
                  currentTenant === 'cn'
                    ? 'border-red-400 bg-red-50 ring-2 ring-red-200'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  currentTenant === 'cn' ? 'bg-red-100' : 'bg-gray-100'
                }`}>
                  <MapPin size={24} className={currentTenant === 'cn' ? 'text-red-500' : 'text-gray-500'} />
                </div>
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">🇨🇳</span>
                    <span className="font-semibold text-gray-800">{lang === 'zh' ? '国内' : 'China'}</span>
                    {currentTenant === 'cn' && (
                      <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                        {lang === 'zh' ? '当前' : 'Current'}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {lang === 'zh' ? '中国大陆地区用户' : 'Users in Mainland China'}
                  </p>
                </div>
              </button>
              
              {/* 国际选项 */}
              <button
                onClick={() => handleSwitchTenant('intl')}
                className={`w-full flex items-center gap-4 p-4 border rounded-xl transition-all ${
                  currentTenant === 'intl'
                    ? 'border-blue-400 bg-blue-50 ring-2 ring-blue-200'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  currentTenant === 'intl' ? 'bg-blue-100' : 'bg-gray-100'
                }`}>
                  <Globe size={24} className={currentTenant === 'intl' ? 'text-blue-500' : 'text-gray-500'} />
                </div>
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">🌍</span>
                    <span className="font-semibold text-gray-800">{lang === 'zh' ? '国际' : 'International'}</span>
                    {currentTenant === 'intl' && (
                      <span className="px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">
                        {lang === 'zh' ? '当前' : 'Current'}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {lang === 'zh' ? '海外及港澳台地区用户' : 'Overseas, HK, Macau & Taiwan users'}
                  </p>
                </div>
              </button>
            </div>
            
            {/* 底部按钮 */}
            <div className="px-6 pb-6">
              <button
                onClick={() => setShowTenantModal(false)}
                className="w-full py-3 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {lang === 'zh' ? '取消' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterPage;
